import { useState } from 'react';
import { Select, formatDateTime } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable } from '../components/DataTable.js';
import { useResource } from '../lib/useResource.js';
import { useT } from '../i18n/index.js';

/** Cross-tenant audit trail (docs/07 "/audit"). */

interface AuditRow {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  payload: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
  actor: { name: string; email: string; scope: string } | null;
  tenant: { name: string; slug: string } | null;
}

export function Audit() {
  const t = useT();
  const [entityType, setEntityType] = useState('');
  const params = new URLSearchParams({ pageSize: '100' });
  if (entityType) params.set('entityType', entityType);
  const logs = useResource<AuditRow[]>(`/platform/audit?${params.toString()}`);

  const columns: Column<AuditRow>[] = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="tabular">{formatDateTime(row.createdAt, 'UTC')}</span>,
    },
    { key: 'action', header: 'Action', render: (row) => <span className="font-medium">{row.action}</span> },
    {
      key: 'actor',
      header: 'Who',
      render: (row) =>
        row.actor ? (
          <div>
            <p>{row.actor.name}</p>
            <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
              {row.actor.scope.toLowerCase().replace('_', ' ')}
            </p>
          </div>
        ) : (
          <span style={{ color: 'var(--color-faint)' }}>system</span>
        ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      render: (row) =>
        row.tenant ? row.tenant.name : <span style={{ color: 'var(--color-faint)' }}>platform</span>,
    },
    {
      key: 'entity',
      header: 'Entity',
      render: (row) => (
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {row.entityType}
          {row.entityId ? ` · ${row.entityId.slice(-8)}` : ''}
        </span>
      ),
    },
  ];

  return (
    <Page
      title={t('audit.title')}
      description={t('audit.subtitleLong')}
      actions={
        <Select
          aria-label={t('common.filterEntity')}
          className="h-9 w-44"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
        >
          <option value="">{t('common.allEntities')}</option>
          {['Tenant', 'Contract', 'Invoice', 'TenantDomain', 'User', 'Booking', 'Trip', 'Dispute'].map(
            (entity) => (
              <option key={entity} value={entity}>
                {entity}
              </option>
            ),
          )}
        </Select>
      }
    >
      <DataTable
        columns={columns}
        rows={logs.data}
        rowKey={(row) => row.id}
        loading={logs.loading}
        error={logs.error}
        empty={{ title: 'Nothing recorded yet', description: 'Administrative actions appear here.' }}
      />
    </Page>
  );
}
